import { featureFlags, type FeatureFlagKey } from './feature-flags.config';

/**
 * Cached user email domain for feature flag evaluation.
 * Set via `initFeatureFlagsWithUserEmail()` when the user logs in.
 */
let cachedUserEmailDomain: string | undefined;

/**
 * Cached tenant ID for feature flag evaluation.
 * Set via `initFeatureFlagsWithTenantId()` when the tenant is selected.
 */
let cachedTenantId: string | undefined;

/**
 * Extracts the domain from an email address and caches it for feature flag evaluation.
 * Should be called once when the user logs in.
 *
 * @param email - The user's email address (e.g., 'user@league.com')
 */
export const initFeatureFlagsWithUserEmail = (email: string): void => {
  const parts = email.split('@');
  cachedUserEmailDomain =
    parts.length === 2 ? parts[1].toLowerCase() : undefined;
};

/**
 * Clears the cached user email domain. Useful for testing or logout scenarios.
 */
export const clearCachedUserEmailDomain = (): void => {
  cachedUserEmailDomain = undefined;
};

/**
 * Caches the tenant ID for feature flag evaluation.
 * Should be called when the tenant is selected or changes.
 *
 * @param tenantId - The tenant ID (e.g., 'league')
 */
export const initFeatureFlagsWithTenantId = (tenantId: string): void => {
  cachedTenantId = tenantId || undefined;
};

/**
 * Clears the cached tenant ID. Useful for testing or logout scenarios.
 */
export const clearCachedTenantId = (): void => {
  cachedTenantId = undefined;
};

/**
 * localStorage key for the auto-enabled flags override setting.
 * This is a global setting, not a feature flag.
 */
const AUTO_ENABLE_OVERRIDE_KEY = 'overrideAutoEnabledFlags';

/**
 * Checks if auto-enabled flags are being overridden.
 * When overridden, users can manually toggle all flags regardless of automatic enablement.
 */
export const isAutoEnableOverridden = (): boolean =>
  localStorage.getItem(AUTO_ENABLE_OVERRIDE_KEY) === 'true';

/**
 * Sets whether auto-enabled flags should be overridden.
 * @param override - Whether to override automatic enablement
 */
export const setAutoEnableOverride = (override: boolean): void => {
  localStorage.setItem(AUTO_ENABLE_OVERRIDE_KEY, String(override));
};

/**
 * Checks if a feature flag is auto-enabled due to the user's email domain.
 * This is useful for UI to show that a flag cannot be toggled off.
 *
 * @param key - The feature flag key to check
 * @returns Whether the flag is enabled due to user email domain matching
 */
export const isFeatureFlagEnabledByUserEmailDomain = (
  key: FeatureFlagKey,
): boolean => {
  const flag = featureFlags.find((f) => f.key === key);
  if (!flag) {
    return false;
  }
  // Hidden flags are not affected by the global auto-enable override.
  if (isAutoEnableOverridden() && !flag.isHiddenFromUi) {
    return false;
  }
  if (!cachedUserEmailDomain) {
    return false;
  }
  return (
    flag.enabledForUserEmailDomains?.includes(cachedUserEmailDomain) ?? false
  );
};

/**
 * Checks if a feature flag is auto-enabled due to the current tenant ID.
 * This is useful for UI to show that a flag cannot be toggled off.
 *
 * @param key - The feature flag key to check
 * @returns Whether the flag is enabled due to tenant matching
 */
export const isFeatureFlagEnabledByTenant = (key: FeatureFlagKey): boolean => {
  const flag = featureFlags.find((f) => f.key === key);
  if (!flag) {
    return false;
  }
  if (isAutoEnableOverridden() && !flag.isHiddenFromUi) {
    return false;
  }
  if (!cachedTenantId) {
    return false;
  }
  return flag.enabledForTenants?.includes(cachedTenantId) ?? false;
};

/**
 * Checks if a feature flag is auto-enabled due to either user email domain or tenant ID.
 * This is useful for UI to show that a flag cannot be toggled off.
 *
 * @param key - The feature flag key to check
 * @returns Whether the flag is auto-enabled
 */
export const isFeatureFlagAutoEnabled = (key: FeatureFlagKey): boolean =>
  isFeatureFlagEnabledByUserEmailDomain(key) ||
  isFeatureFlagEnabledByTenant(key);

/**
 * Gets the current value of a feature flag.
 *
 * A flag is considered enabled if:
 * 1. The user has explicitly enabled it via localStorage, OR
 * 2. The user's email domain matches one of the flag's `enabledForUserEmailDomains`, OR
 * 3. The current tenant ID matches one of the flag's `enabledForTenants`
 *
 * @param key - The feature flag key to check
 * @returns Whether the feature flag is enabled
 */
export const getFeatureFlag = (key: FeatureFlagKey): boolean =>
  localStorage.getItem(key) === 'true' ||
  isFeatureFlagEnabledByUserEmailDomain(key) ||
  isFeatureFlagEnabledByTenant(key);

/**
 * Returns the current state of all feature flags as a key-value record.
 * Useful for passing feature flag state into systems that need a snapshot
 * of all flags (e.g., schema conditional evaluation via `_featureFlags`).
 */
export const getAllFeatureFlags = (): Record<FeatureFlagKey, boolean> =>
  Object.fromEntries(
    featureFlags.map((flag) => [flag.key, getFeatureFlag(flag.key)]),
  ) as Record<FeatureFlagKey, boolean>;

export const setFeatureFlag = (key: FeatureFlagKey, value: boolean): void => {
  localStorage.setItem(key, value ? 'true' : 'false');
};

export const clearFeatureFlag = (key: FeatureFlagKey): void => {
  localStorage.removeItem(key);
};
